import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDatasets } from "../context/DatasetContext";
import { Search, User, Building2, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (val) => {
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
  return `$${val.toFixed(0)}`;
};

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const { selectedDatasets } = useDatasets();
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const datasetsRef = useRef(selectedDatasets);

  // Keep ref in sync without triggering re-renders of doSearch
  useEffect(() => { datasetsRef.current = selectedDatasets; }, [selectedDatasets]);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const params = { q };
      const ds = datasetsRef.current;
      if (ds.length > 0) params.dataset_ids = ds.join(",");
      const { data } = await axios.get(`${API}/search`, { params });
      setResults(data);
      setSelectedIdx(-1);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => { doSearch(query); setOpen(true); }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (result) => {
    setOpen(false);
    setQuery("");
    if (result.type === "donor") {
      navigate(`/data?donor=${encodeURIComponent(result.name)}`);
    } else {
      navigate(`/data?recipient=${encodeURIComponent(result.name)}`);
    }
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && selectedIdx >= 0) { e.preventDefault(); handleSelect(results[selectedIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search donors or recipients..."
          className="w-full h-9 pl-9 pr-8 rounded-md border border-border bg-muted/40 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setResults([]); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="global-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-popover border border-border rounded-md shadow-lg overflow-hidden animate-fade-in" data-testid="global-search-results">
          {/* Scope hint — makes it obvious what pool the results came from.
              Users were confused when they got hits with zero datasets checked
              in the sidebar; the search silently spans everything they can
              access. This small badge answers the question up-front. */}
          <div
            className="px-3 py-1.5 text-[11px] uppercase tracking-wider border-b border-border/60 bg-muted/40 flex items-center justify-between"
            data-testid="global-search-scope"
          >
            <span className="text-muted-foreground">
              Searching {selectedDatasets.length > 0
                ? <><b className="text-foreground">{selectedDatasets.length}</b> selected dataset{selectedDatasets.length > 1 ? "s" : ""}</>
                : <b className="text-foreground">all datasets</b>}
            </span>
            {selectedDatasets.length === 0 && (
              <span className="text-[10px] text-muted-foreground/70 normal-case tracking-normal italic">
                check a dataset to narrow
              </span>
            )}
          </div>
          {loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">Searching...</div>
          )}
          {!loading && results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">No results found</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.name}`}
              onClick={() => handleSelect(r)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                i === selectedIdx ? "bg-accent" : "hover:bg-muted/60"
              }`}
              data-testid={`search-result-${i}`}
            >
              {r.type === "donor" ? (
                <User className="w-4 h-4 text-blue-500 shrink-0" />
              ) : (
                <Building2 className="w-4 h-4 text-amber-500 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{r.name}</p>
                <p className="text-xs text-muted-foreground">
                  {r.type === "donor" ? "Donor" : "Recipient"} &middot; {r.count.toLocaleString()} donations
                </p>
              </div>
              <span className="text-sm font-semibold text-emerald-600 shrink-0">
                {formatCurrency(r.total_amount)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
